import type { SubmissionStatus } from "./types";

/** One submission as the dashboard needs it: status and the times, in epoch ms, that it has reached. */
export interface StatRow {
    status: SubmissionStatus;
    submittedAt: number;
    respondByAt: number | null;
    dueAt: number | null;
    returnedAt: number | null;
    clipCount: number;
    totalSec: number;
}

export interface CoachStats {
    /** Sent and not yet answered, and the earliest answer-by time among them. */
    waiting: number;
    nextRespondBy: number | null;
    /** Accepted or being reviewed, the earliest due time, and how many are past it. */
    inProgress: number;
    nextDue: number | null;
    overdue: number;
    returned: number;
    /** Returned since `monthStart`. */
    returnedThisMonth: number;
    /** Returned reviews that landed by their due time, as a fraction, or null before any review. */
    onTimeRate: number | null;
    /** Mean time from sending to return, or null before any review. */
    avgTurnaroundMs: number | null;
    clipsReviewed: number;
    footageSec: number;
    declined: number;
    expired: number;
}

const IN_PROGRESS: readonly SubmissionStatus[] = ["accepted", "in_review"];

const min = (xs: number[]): number | null => (xs.length === 0 ? null : Math.min(...xs));

/** The coach's numbers from their submissions. Pure: `now` and `monthStart` are passed in. */
export function coachStats(rows: readonly StatRow[], now: number, monthStart: number): CoachStats {
    const waiting = rows.filter((r) => r.status === "submitted");
    const active = rows.filter((r) => IN_PROGRESS.includes(r.status));
    const done = rows.filter((r) => r.status === "returned" && r.returnedAt !== null);
    const judged = done.filter((r) => r.dueAt !== null);
    const turnarounds = done.map((r) => (r.returnedAt as number) - r.submittedAt);
    return {
        waiting: waiting.length,
        nextRespondBy: min(waiting.map((r) => r.respondByAt).filter((t): t is number => t !== null)),
        inProgress: active.length,
        nextDue: min(active.map((r) => r.dueAt).filter((t): t is number => t !== null)),
        overdue: active.filter((r) => r.dueAt !== null && r.dueAt < now).length,
        returned: done.length,
        returnedThisMonth: done.filter((r) => (r.returnedAt as number) >= monthStart).length,
        onTimeRate: judged.length === 0 ? null : judged.filter((r) => (r.returnedAt as number) <= (r.dueAt as number)).length / judged.length,
        avgTurnaroundMs: turnarounds.length === 0 ? null : turnarounds.reduce((a, b) => a + b, 0) / turnarounds.length,
        clipsReviewed: done.reduce((n, r) => n + r.clipCount, 0),
        footageSec: done.reduce((n, r) => n + r.totalSec, 0),
        declined: rows.filter((r) => r.status === "declined").length,
        expired: rows.filter((r) => r.status === "expired").length,
    };
}

/** "1.8 days", "14 hours" or "1 hour", for the average turnaround. */
export function turnaroundMsText(ms: number): string {
    const hours = Math.max(1, Math.round(ms / 3_600_000));
    if (hours < 36) return hours === 1 ? "1 hour" : `${hours} hours`;
    const days = Math.round((hours / 24) * 10) / 10;
    return days === 1 ? "1 day" : `${days} days`;
}
